import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle, Table } from './shared';

export function ConfigContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">网关配置</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          了解如何配置 OpenClaw 网关的各项参数，包括端口、认证、限流、CORS 等设置。
        </p>
      </div>

      <SectionTitle id="config-file">配置文件</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        网关使用 YAML 格式的配置文件，默认位置为 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">~/.openclaw/gateway.yaml</code>。
      </p>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml - 完整配置示例
gateway:
  # 基础配置
  port: 3000
  host: "0.0.0.0"
  base_path: "/api"
  
  # SSL/TLS 配置
  ssl:
    enabled: false
    cert_file: "/path/to/cert.pem"
    key_file: "/path/to/key.pem"
    
  # 认证配置
  auth:
    enabled: true
    type: "api_key"  # api_key, jwt, oauth2, mtls
    api_key:
      header: "Authorization"  # 或 "X-API-Key"
      prefix: "Bearer"
    jwt:
      secret: "your-jwt-secret"
      algorithm: "HS256"
      expiration: 3600
    oauth2:
      provider: "github"
      client_id: "your-client-id"
      client_secret: "your-client-secret"
      redirect_uri: "http://localhost:3000/callback"
      
  # 限流配置
  rate_limit:
    enabled: true
    algorithm: "sliding_window"  # fixed_window, sliding_window, token_bucket, leaky_bucket
    requests_per_minute: 60
    burst: 10
    storage: "memory"  # memory, redis
    redis:
      host: "localhost"
      port: 6379
      db: 0
      
  # CORS 配置
  cors:
    enabled: true
    origins: ["*"]
    methods: ["GET", "POST", "PUT", "DELETE", "OPTIONS"]
    headers: ["Content-Type", "Authorization"]
    credentials: true
    max_age: 86400
    
  # 超时配置
  timeout:
    read: 30
    write: 30
    idle: 120
    
  # 日志配置
  logging:
    level: "info"  # debug, info, warn, error
    format: "json"  # json, text
    output: "/var/log/openclaw/gateway.log"
    max_size: 100  # MB
    max_backups: 10
    max_age: 30  # days
    compress: true
    
  # 监控配置
  metrics:
    enabled: true
    port: 9090
    path: "/metrics"
    
  # 健康检查
  health:
    enabled: true
    path: "/health"
    interval: 10  # seconds`}
      />

      <SectionTitle id="basic-config">基础配置</SectionTitle>
      <SubsectionTitle id="server-config">服务器配置</SubsectionTitle>
      <Table
        headers={['参数', '类型', '默认值', '说明']}
        rows={[
          ['port', 'integer', '3000', '网关监听端口'],
          ['host', 'string', '0.0.0.0', '监听地址，0.0.0.0 表示所有网卡'],
          ['base_path', 'string', '/api', 'API 基础路径'],
          ['workers', 'integer', 'CPU 核心数', '工作进程数量']
        ]}
      />

      <SubsectionTitle id="ssl-config">SSL/TLS 配置</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        启用 HTTPS 需要提供证书和私钥文件：
      </p>
      <CodeBlock
        language="yaml"
        code={`ssl:
  enabled: true
  cert_file: "/etc/openclaw/ssl/cert.pem"
  key_file: "/etc/openclaw/ssl/key.pem"
  # 可选：CA 证书（用于客户端证书验证）
  ca_file: "/etc/openclaw/ssl/ca.pem"
  # 可选：最低 TLS 版本
  min_version: "1.2"  # 1.0, 1.1, 1.2, 1.3`}
      />

      <InfoBox type="info" title="获取 SSL 证书">
        可以使用 Let's Encrypt 免费获取 SSL 证书：<br />
        <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">certbot certonly --standalone -d yourdomain.com</code>
      </InfoBox>

      <SectionTitle id="route-config">路由配置</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        可以为不同的路径配置不同的智能体和策略：
      </p>
      <CodeBlock
        language="yaml"
        code={`# routes.yaml
routes:
  # 默认聊天接口
  - path: "/api/chat"
    method: "POST"
    agent: "default"
    auth_required: true
    rate_limit: 30  # 覆盖全局限流
    timeout: 60
    
  # 流式聊天接口
  - path: "/api/chat/stream"
    method: "POST"
    agent: "default"
    auth_required: true
    stream: true
    
  # Webhook 接口（无需认证）
  - path: "/api/webhook"
    method: "POST"
    agent: "webhook_handler"
    auth_required: false
    
  # 管理接口（需要管理员权限）
  - path: "/api/admin/*"
    method: "*"
    agent: "admin"
    auth_required: true
    roles: ["admin"]
    
  # 公开接口
  - path: "/api/public/*"
    method: "GET"
    auth_required: false
    cache:
      enabled: true
      ttl: 300  # 缓存 5 分钟`}
      />

      <SubsectionTitle id="route-matching">路由匹配规则</SubsectionTitle>
      <ul className="space-y-2 text-slate-300">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1"></i>
          <span><strong>精确匹配：</strong><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">/api/chat</code> 只匹配该路径</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1"></i>
          <span><strong>前缀匹配：</strong><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">/api/admin/*</code> 匹配所有以 /api/admin/ 开头的路径</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1"></i>
          <span><strong>参数匹配：</strong><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">/api/users/:id</code> 匹配 /api/users/123</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1"></i>
          <span><strong>正则匹配：</strong><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">/api/files/.*\\.pdf</code> 匹配所有 PDF 文件</span>
        </li>
      </ul>

      <SectionTitle id="env-config">环境变量配置</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        除了配置文件，也可以使用环境变量进行配置（优先级高于配置文件）：
      </p>
      <CodeBlock
        language="bash"
        code={`# .env
GATEWAY_PORT=3000
GATEWAY_HOST=0.0.0.0
GATEWAY_BASE_PATH=/api

# 认证配置
GATEWAY_AUTH_ENABLED=true
GATEWAY_AUTH_TYPE=api_key
API_KEY_SECRET=your-secret-key
JWT_SECRET=your-jwt-secret

# 限流配置
GATEWAY_RATE_LIMIT_ENABLED=true
GATEWAY_RATE_LIMIT_RPM=60
GATEWAY_RATE_LIMIT_BURST=10

# Redis 配置（用于分布式限流）
REDIS_HOST=localhost
REDIS_PORT=6379
REDIS_DB=0
REDIS_PASSWORD=

# 日志配置
GATEWAY_LOG_LEVEL=info
GATEWAY_LOG_FORMAT=json
GATEWAY_LOG_OUTPUT=/var/log/openclaw/gateway.log`}
      />

      <SectionTitle id="advanced-config">高级配置</SectionTitle>
      <SubsectionTitle id="load-balancing">负载均衡</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        当有多个智能体实例时，可以配置负载均衡策略：
      </p>
      <CodeBlock
        language="yaml"
        code={`load_balancing:
  enabled: true
  algorithm: "round_robin"  # round_robin, least_connections, ip_hash, random
  health_check:
    enabled: true
    interval: 10  # seconds
    timeout: 5
    unhealthy_threshold: 3
    healthy_threshold: 2
  
  # 后端服务列表
  backends:
    - url: "http://agent1:8080"
      weight: 1
    - url: "http://agent2:8080"
      weight: 2
    - url: "http://agent3:8080"
      weight: 1`}
      />

      <SubsectionTitle id="circuit-breaker">熔断器</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        当后端服务出现故障时，自动熔断以保护系统：
      </p>
      <CodeBlock
        language="yaml"
        code={`circuit_breaker:
  enabled: true
  # 错误率阈值（百分比）
  error_threshold: 50
  # 最小请求数（达到此数量后才开始计算错误率）
  min_requests: 10
  # 熔断持续时间（秒）
  timeout: 60
  # 半开状态允许的请求数
  half_open_requests: 3`}
      />

      <SubsectionTitle id="caching">缓存配置</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`cache:
  enabled: true
  backend: "redis"  # memory, redis
  ttl: 300  # 默认缓存时间（秒）
  
  # 缓存规则
  rules:
    - path: "/api/public/*"
      methods: ["GET"]
      ttl: 600
      
    - path: "/api/data/*"
      methods: ["GET"]
      ttl: 300
      # 缓存键包含查询参数
      include_query: true
      
  redis:
    host: "localhost"
    port: 6379
    db: 1
    key_prefix: "gateway:cache:"`}
      />

      <InfoBox type="warning" title="注意">
        缓存配置需要谨慎使用，确保不会缓存敏感数据或频繁变化的数据。建议只对公开的、变化较少的数据启用缓存。
      </InfoBox>

      <SectionTitle id="config-validation">配置验证</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        在启动网关前，可以验证配置文件是否正确：
      </p>
      <CodeBlock
        language="bash"
        code={`# 验证配置文件
openclaw gateway config validate

# 查看当前配置
openclaw gateway config show

# 测试配置（不实际启动）
openclaw gateway config test`}
      />

      <SectionTitle id="hot-reload">热重载</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        修改配置文件后，可以热重载配置而无需重启网关：
      </p>
      <CodeBlock
        language="bash"
        code={`# 重载配置
openclaw gateway reload

# 或发送 SIGHUP 信号
kill -HUP $(cat /var/run/openclaw-gateway.pid)`}
      />

      <InfoBox type="info" title="支持热重载的配置">
        以下配置支持热重载：限流参数、路由规则、日志级别、CORS 设置。<br />
        不支持热重载的配置：端口、SSL 证书、认证类型（需要重启）。
      </InfoBox>
    </div>
  );
}

export default ConfigContent;